'use client';

import {
  ParticipantTile,
  useEnsureTrackRef,
  type ParticipantTileProps,
} from '@livekit/components-react';
import { Track } from 'livekit-client';
import * as React from 'react';
import { createPortal } from 'react-dom';
import { raisedHandRank } from '@/lib/raise-hand';
import { useRaisedHandQueue } from '@/lib/useRaiseHand';
import { ru } from '@/lib/ru';
import { RaiseHandIcon } from './RaiseHandIcon';

import { SelfVideoCollapseContext } from './SelfVideoCollapseContext';

function SelfVideoToggle() {
  const { collapsed, toggle } = React.useContext(SelfVideoCollapseContext);
  return (
    <button
      type="button"
      className="agima-self-video-toggle"
      aria-label={collapsed ? ru.expandSelfVideo : ru.collapseSelfVideo}
      title={collapsed ? ru.expandSelfVideo : ru.collapseSelfVideo}
      onClick={toggle}
    >
      {collapsed ? '↗' : '↘'}
    </button>
  );
}

export function AgimaParticipantTile(props: ParticipantTileProps) {
  const tileRef = React.useRef<HTMLDivElement>(null);
  const trackRef = useEnsureTrackRef(props.trackRef);
  return (
    <div className="agima-participant-tile" ref={tileRef}>
      <ParticipantTile {...props} />
      {trackRef.participant.isLocal && trackRef.source === Track.Source.Camera && (
        <SelfVideoToggle />
      )}
      <ParticipantRaiseHandBadge tileRef={tileRef} trackRef={props.trackRef} />
    </div>
  );
}

function ParticipantRaiseHandBadge(props: {
  tileRef: React.RefObject<HTMLDivElement | null>;
  trackRef?: ParticipantTileProps['trackRef'];
}) {
  const trackRef = useEnsureTrackRef(props.trackRef);
  const queue = useRaisedHandQueue();
  const rank = raisedHandRank(queue, trackRef.participant.identity);
  const visible = rank !== null && trackRef.source !== Track.Source.ScreenShare;
  const [host, setHost] = React.useState<HTMLElement | null>(null);

  React.useLayoutEffect(() => {
    if (!visible) {
      setHost(null);
      return;
    }

    const findHost = () => {
      const metadata = props.tileRef.current?.querySelector('.lk-participant-metadata');
      return metadata instanceof HTMLElement ? metadata : null;
    };

    const found = findHost();
    if (found) {
      setHost(found);
      return;
    }

    const frame = requestAnimationFrame(() => setHost(findHost()));
    return () => cancelAnimationFrame(frame);
  }, [props.tileRef, visible, trackRef.participant.identity]);

  if (!visible || !host) return null;

  return createPortal(
    <div className="agima-raise-hand-badge" aria-label={`${ru.raisedHand} ${rank}`}>
      <RaiseHandIcon />
      <span className="agima-raise-hand-badge-rank">{rank}</span>
    </div>,
    host,
  );
}
